import pluralize from 'pluralize';
import React from 'react';
import { InView } from 'react-intersection-observer';

import { CommentsContextProvider } from '@contexts/CommentsContext';
import useEventTracking from '@hooks/useEventTracking';
import { TrackingLocation } from '@tracking';
import { CommentableType } from '@utils/backend-api/comments';

import LazyContent from './LazyContent';

interface CommentsContentProps {
  commentableId: number;
  commentableType: CommentableType;
  commentCount?: number;
  overrideLazyLoad?: boolean;
}

const COMMENT_ID = 'commentId';

export default function CommentsContent({
  commentableId,
  commentableType,
  commentCount,
  overrideLazyLoad,
}: CommentsContentProps) {
  const searchParams = new URLSearchParams(window.location.search);
  const searchParamCommentId = searchParams.get(COMMENT_ID);

  const { Track } = useEventTracking({
    location: TrackingLocation.CommentSection,
  });

  return (
    <CommentsContextProvider
      rootCommentableId={commentableId}
      rootCommentableType={commentableType}
    >
      <Track>
        <InView rootMargin="100px" triggerOnce>
          {({ inView, ref }) => (
            <div id="comments-section" className="mb-80 sm:mb-112" ref={ref}>
              <div className="flex items-center justify-between border-0 border-b border-solid border-grayscale-gray pb-12">
                <h3 className="m-0 font-suisse-neue text-body-lg font-normal text-black">
                  Conversation
                </h3>
                {commentCount > 0 && (
                  <span className="font-suisse-intl-mono text-overline-md text-primary-magenta">
                    {commentCount} {pluralize('comment', commentCount)}
                  </span>
                )}
              </div>
              <>
                {(inView || searchParamCommentId || overrideLazyLoad) && (
                  <LazyContent
                    commentId={searchParamCommentId}
                    commentableId={commentableId}
                    commentableType={commentableType}
                  />
                )}
              </>
            </div>
          )}
        </InView>
      </Track>
    </CommentsContextProvider>
  );
}
